import React from 'react';
import { Collapse, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';

import { sourceLogo, sourceShortName } from '../common/sources';
import ItemTagChip from './ItemTagChip';

// The unit each counter is quoted in, and the value it has to clear to be
// worth a mention.
const COUNTERS = {
  popularity: { unit: 'pts' },
  engagement: { unit: 'cmts' },
  numPosts: { unit: 'posts', over: 1, localize: false },
  views: { unit: 'views' },
  likes: { unit: 'likes' },
  comments: { unit: 'cmts' },
  stars: { unit: 'stars' },
  forks: { unit: 'forks' },
  issues: { unit: 'issues' },
};

// Points, comments and post count — what every discussion site reports.
const DISCUSSION = ['popularity', 'engagement', 'numPosts'];
const DISCUSSION_NORMS = ['popularityNorm', 'engagementNorm'];

// One row per source, in this order.
const SOURCES = [
  { key: 'hn', counters: DISCUSSION, norms: DISCUSSION_NORMS },
  {
    key: 'yt',
    counters: ['views', 'likes', 'comments'],
    norms: ['viewsNorm', 'likesNorm', 'commentsNorm'],
  },
  {
    key: 'gh',
    counters: ['stars', 'forks', 'issues'],
    norms: ['starsNorm', 'forksNorm', 'issuesNorm'],
  },
  { key: 'tw', counters: DISCUSSION, norms: DISCUSSION_NORMS },
  { key: 'rd', counters: DISCUSSION, norms: [] },
  { key: 'lb', counters: DISCUSSION, norms: [] },
];

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 5,
  overflowY: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
}));

function countLine(stats, counters) {
  return counters
    .map((name) => {
      const { unit, over = 0, localize = true } = COUNTERS[name];
      const value = stats[name];
      if (!(value > over)) return null;
      return `${localize ? value.toLocaleString() : value} ${unit}`;
    })
    .filter(Boolean)
    .join(' · ');
}

// A norm of zero drops out along with the missing ones — it carries no signal.
function normLine(stats, norms) {
  return norms
    .map((name) => stats[name])
    .filter(Boolean)
    .map((n) => n.toFixed(3))
    .join(' / ');
}

function SourceRow({ source, stats }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <img src={sourceLogo(source.key)} alt={sourceShortName(source.key)} style={{ width: 14, height: 14 }} />
      <Typography variant="body2">{countLine(stats, source.counters)}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {normLine(stats, source.norms)}
      </Typography>
    </Box>
  );
}

/**
 * The panel the heat/buzz badge opens: each source's raw and normalized stats,
 * and any related items. Slides up over the card body, so it is positioned
 * against the content region rather than the card.
 */
export default function ExpandPanel({
  item, expanded, heatScore, buzzScore, onItemRead,
}) {
  const stats = item.extra?.stats;

  return (
    <StyledCollapse in={expanded} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          { stats && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              { heatScore != null && buzzScore != null && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  🔥 {heatScore.toFixed(3)} / {Number(item.extra.heatSort).toLocaleString()} &nbsp;·&nbsp; ⚡ {buzzScore.toFixed(3)} / {Number(item.extra.buzzSort).toLocaleString()}
                </Typography>
              )}
              {SOURCES.filter((source) => stats[source.key]).map((source) => (
                <SourceRow key={source.key} source={source} stats={stats[source.key]} />
              ))}
            </Box>
          )}
        </Box>
        {item.relatedItems
        && (
          <Box>
            <Typography component="b">Related items:</Typography>
            {item.relatedItems.map((ri) => (
              <ItemTagChip
                key={ri.id}
                label={ri.id}
                variant="default"
                onClick={() => onItemRead(ri)}
              />
            ))}
          </Box>
        )}
      </Box>
    </StyledCollapse>
  );
}
